import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { GoogleMap } from '../components/maps/GoogleMap';
import { AddressAutocomplete } from '../components/maps/AddressAutocomplete';
import {
  MapPin,
  Navigation,
  User,
  Clock,
  Search,
  Filter,
  RefreshCw,
  Phone,
  Calendar,
  AlertTriangle,
  Route
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusColors = {
  active: 'bg-green-500',
  available: 'bg-green-500',
  busy: 'bg-yellow-500',
  offline: 'bg-gray-400',
  on_route: 'bg-blue-500'
};

const getStatusLabel = (status, t) => {
  const labels = {
    active: t('specialistStatus.active'),
    available: t('specialistStatus.available'),
    busy: t('specialistStatus.busy'),
    offline: t('specialistStatus.offline'),
    on_route: t('specialistStatus.on_route')
  };
  return labels[status] || status;
};

const PriorityColors = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  normal: 'bg-blue-500',
  low: 'bg-gray-400'
};

const SpecialistCard = ({ specialist, selected, onClick, t }) => (
  <div
    className={`p-3 rounded-lg cursor-pointer transition-all ${
      selected
        ? 'bg-[#FDE8EA] border-2 border-[#E63946]'
        : 'bg-white border border-gray-200 hover:border-[#E63946]'
    }`}
    onClick={onClick}
  >
    <div className="flex items-center gap-3">
      <div className="relative">
        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
          <User className="h-5 w-5 text-blue-600" />
        </div>
        <div
          className={`absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-white ${
            StatusColors[specialist.status] || 'bg-gray-400'
          }`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">
          {specialist.first_name} {specialist.last_name}
        </p>
        <p className="text-xs text-gray-500">{specialist.specialist_type}</p>
      </div>
    </div>
    {specialist.current_location && (
      <div className="mt-2 text-xs text-gray-400 flex items-center gap-1">
        <Clock className="h-3 w-3" />
        {t('specialists.lastUpdate')}{' '}
        {new Date(specialist.current_location.timestamp).toLocaleTimeString(undefined, {
          hour: '2-digit',
          minute: '2-digit'
        })}
      </div>
    )}
  </div>
);

const OrderMarkerCard = ({ order, t }) => (
  <div className="p-3 bg-white rounded-lg shadow-lg border max-w-xs">
    <div className="flex items-start justify-between mb-2">
      <Badge className={`${PriorityColors[order.priority]} text-white`}>
        {t(`priority.${order.priority}`)}
      </Badge>
      <Badge variant="outline">{order.status}</Badge>
    </div>
    <p className="font-medium text-sm">{order.patient_name}</p>
    <p className="text-xs text-gray-500 mt-1">{order.service_address}</p>
    {order.scheduled_date && (
      <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
        <Calendar className="h-3 w-3" />
        {order.scheduled_date} {order.scheduled_time}
      </p>
    )}
    {order.specialist_name && (
      <p className="text-xs text-blue-600 mt-1">
        <User className="h-3 w-3 inline mr-1" />
        {order.specialist_name}
      </p>
    )}
  </div>
);

const MapPlaceholder = ({ specialists, orders, selectedSpecialist, selectedOrder, onMarkerClick }) => {
  return (
    <GoogleMap
      specialists={specialists}
      orders={orders}
      selectedSpecialist={selectedSpecialist}
      selectedOrder={selectedOrder}
      onMarkerClick={onMarkerClick}
      showTraffic={false}
      className="h-full min-h-[500px]"
      data-testid="geolocation-map"
    />
  );
};

export const GeolocationPage = () => {
  const { t } = useTranslation('geolocation');
  const [specialists, setSpecialists] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedSpecialist, setSelectedSpecialist] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [searchAddress, setSearchAddress] = useState('');
  const [geocodeResult, setGeocodeResult] = useState(null);

  useEffect(() => {
    fetchData();
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [statusFilter, dateFilter]);

  const fetchData = async () => {
    try {
      const [specialistsRes, ordersRes] = await Promise.all([
        axios.get(`${API_URL}/api/geolocation/specialists`, {
          params: { status: statusFilter !== 'all' ? statusFilter : undefined }
        }),
        axios.get(`${API_URL}/api/geolocation/orders`, {
          params: { 
            date: dateFilter || undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined
          }
        })
      ]);
      setSpecialists(specialistsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGeocode = async () => {
    if (!searchAddress.trim()) return;
    
    try {
      const response = await axios.post(`${API_URL}/api/geolocation/geocode`, null, {
        params: { address: searchAddress }
      });
      setGeocodeResult(response.data);
      toast.success(t('messages.addressFound'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.errorGeocoding'));
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    fetchData();
    toast.success(t('messages.locationsUpdated'));
  };

  return (
    <div className="space-y-6" data-testid="geolocation-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-gray-500 mt-1">{t('subtitle')}</p>
        </div>
        <Button onClick={handleRefresh} disabled={loading} data-testid="refresh-locations-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('refresh')}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('stats.activeSpecialists')}</p>
                <p className="text-2xl font-bold text-green-600">
                  {specialists.filter(s => s.current_location).length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <User className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('stats.ordersOnMap')}</p>
                <p className="text-2xl font-bold text-blue-600">{orders.length}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <MapPin className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('stats.urgentOrders')}</p>
                <p className="text-2xl font-bold text-red-600">
                  {orders.filter(o => o.priority === 'urgent').length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t('stats.onRoute')}</p>
                <p className="text-2xl font-bold text-orange-600">
                  {orders.filter(o => o.status === 'in_progress').length}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Route className="h-5 w-5 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium">{t('filters.label')}</span>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder={t('filters.status')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('filters.all')}</SelectItem>
                <SelectItem value="active">{t('filters.active')}</SelectItem>
                <SelectItem value="in_progress">{t('filters.inProgress')}</SelectItem>
                <SelectItem value="assigned">{t('filters.assigned')}</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-auto"
            />
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <Input
                placeholder={t('filters.searchAddress')}
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleGeocode()}
                className="w-64"
                data-testid="geocode-search-input"
              />
              <Button variant="outline" onClick={handleGeocode} data-testid="geocode-btn">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {geocodeResult && (
            <div className="mt-3 p-3 bg-green-50 rounded-lg">
              <p className="text-sm font-medium text-green-800">{geocodeResult.address}</p>
              <p className="text-xs text-green-600">
                {t('geocode.coordinates')}: {geocodeResult.latitude}, {geocodeResult.longitude}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Specialists List */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">{t('specialists.title')}</CardTitle>
            <CardDescription>
              {specialists.filter(s => s.current_location).length} {t('specialists.withActiveLocation')}
            </CardDescription>
          </CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto space-y-2">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
              </div>
            ) : specialists.length > 0 ? (
              specialists.map(specialist => (
                <SpecialistCard
                  key={specialist.id}
                  specialist={specialist}
                  selected={selectedSpecialist?.id === specialist.id}
                  onClick={() => setSelectedSpecialist(
                    selectedSpecialist?.id === specialist.id ? null : specialist
                  )}
                  t={t}
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <User className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>{t('specialists.noSpecialists')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Map */}
        <Card className="lg:col-span-3 min-h-[500px]">
          <CardContent className="p-0 h-full">
            <MapPlaceholder
              specialists={specialists}
              orders={orders}
              selectedSpecialist={selectedSpecialist}
              selectedOrder={null}
              onMarkerClick={(marker) => {
                if (marker.type === 'specialist') {
                  setSelectedSpecialist(marker.data);
                }
              }}
            />
          </CardContent>
        </Card>
      </div>

      {/* Orders List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('orders.title')}</CardTitle>
          <CardDescription>{t('orders.ordersWithLocation')} {dateFilter}</CardDescription>
        </CardHeader>
        <CardContent>
          {orders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3 text-sm font-medium text-gray-500">{t('orders.table.patient')}</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-gray-500">{t('orders.table.address')}</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-gray-500">{t('orders.table.schedule')}</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-gray-500">{t('orders.table.specialist')}</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-gray-500">{t('orders.table.status')}</th>
                    <th className="text-left py-2 px-3 text-sm font-medium text-gray-500">{t('orders.table.priority')}</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order.id} className="border-b hover:bg-gray-50">
                      <td className="py-2 px-3 text-sm">{order.patient_name}</td>
                      <td className="py-2 px-3 text-sm text-gray-600">
                        {order.service_address}, {order.service_city}
                      </td>
                      <td className="py-2 px-3 text-sm">{order.scheduled_time || '-'}</td>
                      <td className="py-2 px-3 text-sm">{order.specialist_name || t('orders.unassigned')}</td>
                      <td className="py-2 px-3">
                        <Badge variant="outline">{order.status}</Badge>
                      </td>
                      <td className="py-2 px-3">
                        <Badge className={`${PriorityColors[order.priority]} text-white`}>
                          {order.priority}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>{t('orders.noOrders')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GeolocationPage;
